import { Cross1Icon } from '@radix-ui/react-icons';
import { Flex, IconButton } from '@radix-ui/themes';
import { RowSelectionState, Table } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { UseSelectionResult } from '../hooks';
import { SelectionDropdown } from './SelectionDropdown';

interface TableSelectionControlProps<TData extends { id: string }> {
    table: Table<TData>;
    selectionState: UseSelectionResult<TData>;
    displayKey: keyof TData;
}

export const TableSelectionControl = <TData extends { id: string }>({
    table,
    selectionState,
    displayKey,
}: TableSelectionControlProps<TData>) => {
    const { rowSelection, clearSelection, onRowSelectionChange } =
        selectionState;

    const allSelectedRowIds = useMemo(
        () => Object.keys(rowSelection),
        [rowSelection],
    );
    const selectionCount = allSelectedRowIds.length;

    const handleToggleRowSelection = useCallback(
        (rowId: string) => {
            const newResolvedState: RowSelectionState = { ...rowSelection };
            newResolvedState[rowId] = !newResolvedState[rowId];

            onRowSelectionChange(
                newResolvedState,
                table.getRowModel().rows.map((r) => r.original),
            );
        },
        [onRowSelectionChange, rowSelection, table],
    );

    return (
        <Flex gap={'2'} align={'center'}>
            <SelectionDropdown
                selectionState={selectionState}
                displayKey={displayKey}
                onToggleRowSelection={handleToggleRowSelection}
            />

            {!!selectionCount && (
                <IconButton
                    size={'1'}
                    variant='ghost'
                    onClick={clearSelection}
                    aria-label='Unselect all'
                    asChild
                >
                    <Cross1Icon />
                </IconButton>
            )}
        </Flex>
    );
};
